import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import { DebtorLogItem } from '../../../api/client';
import { formatDateLong, formatEmailToDisplayName } from '../../../components/Utils';
import { colors } from '../../../colors';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import FilePreviewLink from './FilePreviewLink';

interface Props {
  logItem: DebtorLogItem;
  selected: boolean;
}

const LINE_BREAK_LENGTH = 56;
const MAX_DISPLAY_LENGTH = 215;

export default function DebtorLogItemComponent(props: Props) {
  const text = props.logItem.text ?? "";

  const numberOfLineBreaks = (text.match(/\n/g) || []).length;
  const effectiveLength = text.length + numberOfLineBreaks * LINE_BREAK_LENGTH;

  const isLongText = effectiveLength > MAX_DISPLAY_LENGTH;
  const [showFullText, setShowFullText] = useState(!isLongText);

  const displayedText = showFullText
    ? text
    : shortenText(text, MAX_DISPLAY_LENGTH);

  function shortenText(input: string, maxLen: number) {
    let currentLength = 0;
    let shortened = "";

    for (const char of input) {
      if (char === "\n") {
        currentLength += LINE_BREAK_LENGTH;
      } else {
        currentLength += 1;
      }
      if (currentLength <= maxLen) {
        shortened += char;
      } else {
        break;
      }
    }
    return shortened;
  }

  return (
    <Box sx={{
      width: '100%',
      margin: '10px 0 10px 0'
    }}>
      <Box sx={{
        borderRadius: '1px',
        boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.08)',
        fontSize: '14px',
        margin: '18px 6px 0 6px',
        padding: '8px 8px 2px 8px',
        ...(props.selected === true && { backgroundColor: colors.secondary.lemon }),
        ...(props.selected === false && { backgroundColor: colors.marble })
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          {props.logItem?.debtorClaimNumbers && (
            <Typography sx={{ fontWeight: "bold" }}>
              {props.logItem.debtorClaimNumbers!.join(", ")}
            </Typography>
          )}
        </Box>
        <Typography sx={{ whiteSpace: "pre-wrap" }}>
          {displayedText}
          {isLongText && !showFullText && "..."}
        </Typography>
        {props.logItem.archiveIds ? (
          <Typography>
            {"\nVedlegg: "}
            <FilePreviewLink archiveIds={props.logItem.archiveIds} />
          </Typography>
        ) : null}
        {isLongText && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <ButtonBase
              onClick={() => setShowFullText(!showFullText)}
              aria-label={showFullText ? "see less" : "see more"}
              sx={{
                fontWeight: "bold",
              }}
            >
              {showFullText ? (
                <>
                  <KeyboardDoubleArrowUpIcon sx={{ color: colors.primary.bondiblue}} />
                  Se mindre
                </>
              ) : (
                <>
                  <KeyboardDoubleArrowDownIcon sx={{ color: colors.primary.bondiblue }} />
                  Se mer
                </>
              )}
            </ButtonBase>
          </Box>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 1, mt: 1, mb: '2px' }}>
          <Typography sx={{
            fontSize: '12px',
            fontWeight: '600',
            color: colors.gray,
          }}>
            {props.logItem.registeredBy ? formatEmailToDisplayName(props.logItem.registeredBy) : "-" }
          </Typography>
          <Typography sx={{
            fontSize: '12px',
            color: colors.gray,
            textAlign: 'right',
            minWidth: '124px',
            fontWeight: '600',
          }}>{formatDateLong(props.logItem.registeredAt)}</Typography>
        </Box>
      </Box>
    </Box>
  )
};
